// ============================================================================
// Markdown + math renderer for AI chat replies.
//
// Math is rendered with KaTeX through react-katex. Unlike the old MathJax
// setup (which re-typeset the whole DOM via a MutationObserver and therefore
// flickered while the user typed), each message is a React element: KaTeX runs
// synchronously once, when the message is rendered, and keystrokes in the input
// box never touch these elements.
//
// Prose uses the same minimal Markdown subset UnderstandingModule always used
// (###/#### headings, **bold**, *italic*, line breaks).
// ============================================================================

import React from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

const MATH_SPLIT_RE = /(\$\$[\s\S]*?\$\$|\$[^$\n]+\$|\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\))/;

const MathError: React.FC<{ math: string }> = ({ math }) => (
  <span
    className="katex-error-fallback"
    style={{ color: '#dc2626', fontFamily: 'monospace', fontSize: '0.9em' }}
  >
    {math}
  </span>
);

function proseToHtml(text: string): string {
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  html = html.replace(/^####\s+(.+)$/gm, '<h4>$1</h4>');
  html = html.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/\n/g, '<br>');
  return html;
}

const MarkdownMath: React.FC<{ content: string }> = ({ content }) => {
  const parts = (content || '').split(MATH_SPLIT_RE);

  return (
    <>
      {parts.map((part, index) => {
        if (!part) return null;

        if (part.startsWith('$$') && part.endsWith('$$') && part.length >= 4) {
          const math = part.slice(2, -2).trim();
          return (
            <BlockMath
              key={index}
              math={math}
              renderError={() => <MathError math={math} />}
            />
          );
        }
        if (part.startsWith('\\[') && part.endsWith('\\]')) {
          const math = part.slice(2, -2).trim();
          return (
            <BlockMath
              key={index}
              math={math}
              renderError={() => <MathError math={math} />}
            />
          );
        }
        if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
          const math = part.slice(1, -1).trim();
          return (
            <InlineMath
              key={index}
              math={math}
              renderError={() => <MathError math={math} />}
            />
          );
        }
        if (part.startsWith('\\(') && part.endsWith('\\)')) {
          const math = part.slice(2, -2).trim();
          return (
            <InlineMath
              key={index}
              math={math}
              renderError={() => <MathError math={math} />}
            />
          );
        }
        return (
          <span
            key={index}
            dangerouslySetInnerHTML={{ __html: proseToHtml(part) }}
          />
        );
      })}
    </>
  );
};

export default MarkdownMath;
